/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { WHITELABEL_MODE } from './whitelabel';
import * as mocks from './ai-mocks';
import { getClinicId } from './whitelabel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// httpOnly cookie-based authentication.
// `withCredentials: true` ensures the browser sends the sn_auth cookie
// on every request (subject to CORS + SameSite policy).
//
// Security note (Sprint 9I): the legacy localStorage `sc_token` fallback
// has been REMOVED. All clients must use the httpOnly cookie session set
// by /api/auth/login. If a request 401s, callers should redirect to /login.
const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// AI Usage Counter: ogni richiesta dichiara il tenant (clinica clonata). Il backend
// conteggia e applica il limite mensile SOLO sulle route AI; sulle altre è ignorato.
api.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  if (!config.headers['x-clinic-id']) config.headers['x-clinic-id'] = getClinicId();
  return config;
});

// ---------------------------------------------------------------------------
//  WHITELABEL AI INTERCEPTOR
//  When REACT_APP_MODE=whitelabel, intercept every request to an AI-flavoured
//  endpoint and short-circuit it with a deterministic client-side mock. This
//  keeps recurring costs at zero and guarantees the app remains 100% usable
//  even in air-gapped clinic networks.
// ---------------------------------------------------------------------------

const AI_ROUTE_HANDLERS = [
  // ---- SOAP / Transcription ----
  {
    match: (url, method) => method === 'post' && /\/transcription\/generate-soap$/.test(url),
    handler: (data) => {
      // Prefer explicit text on the payload; fall back to concatenated history.
      const text = (data && (data.text || data.transcript || data.raw_text || data.additional_context)) || '';
      const soap = mocks.mockSoapFormat(text);
      return { soap, ...soap, _mock: true };
    },
  },
  {
    match: (url, method) => method === 'post' && /\/ai\/soap-summary(\/|$)/.test(url),
    handler: (data) => {
      const text = (data && (data.raw_notes || data.text || data.notes)) || '';
      return { soap: mocks.mockSoapFormat(text), _mock: true };
    },
  },
  {
    match: (url, method) => method === 'get' && /\/ai\/soap-summary\/appointment\//.test(url),
    handler: () => ({ soap: null, _mock: true }),
  },
  {
    match: (url, method) => method === 'post' && /\/transcription\/save$/.test(url),
    // In whitelabel mode we do not persist transcripts server-side; we just
    // acknowledge so the UI proceeds. Persistence should be added via the
    // local IndexedDB store if the clinic needs history.
    handler: (data) => ({ ok: true, chunk: data, saved: false, _mock: true }),
  },
  {
    match: (url, method) => method === 'get' && /\/transcription\/appointment\//.test(url),
    handler: () => ({ chunks: [], _mock: true }),
  },

  // ---- Symptom / Triage ----
  {
    match: (url, method) => method === 'post' && /\/(triage|ai\/symptom-checker)\/(session|start|message)/.test(url),
    handler: (data) => mocks.mockSymptomTriage({ message: data?.message || data?.reason || '', history: data?.history || [] }),
  },
  {
    match: (url, method) => method === 'get' && /\/(triage|ai\/symptom-checker)\/session/.test(url),
    handler: () => ({ messages: [], session_id: null, status: 'ended', _mock: true }),
  },

  // ---- Voice Diary ----
  {
    match: (url, method) => method === 'post' && /\/voice-diary\/parse$/.test(url),
    handler: (data) => ({ structured: mocks.mockVoiceDiaryStructure(data?.transcript || ''), _mock: true }),
  },
  {
    match: (url, method) => method === 'get' && /\/voice-diary\/schema$/.test(url),
    handler: () => ({
      fields: [
        { key: 'systolic_bp', label: 'PA sistolica', unit: 'mmHg', hint: 'Es: PA 120' },
        { key: 'diastolic_bp', label: 'PA diastolica', unit: 'mmHg', hint: 'Es: 80' },
        { key: 'heart_rate', label: 'Frequenza cardiaca', unit: 'bpm', hint: 'Es: FC 72' },
        { key: 'temperature_c', label: 'Temperatura', unit: '°C', hint: 'Es: 36.8' },
        { key: 'spo2', label: 'Saturazione O2', unit: '%', hint: 'Es: 98' },
        { key: 'glucose_mg_dl', label: 'Glicemia', unit: 'mg/dL', hint: 'Es: 92' },
        { key: 'weight_kg', label: 'Peso', unit: 'kg', hint: 'Es: 72' },
      ],
      _mock: true,
    }),
  },

  // ---- Wound Care ----
  {
    match: (url, method) => method === 'post' && /\/wound-care\/(analy|assess)/.test(url),
    handler: (data) => mocks.mockWoundCareAnalysis({ filename: data?.filename, notes: data?.notes }),
  },

  // ---- Drug interactions ----
  {
    match: (url, method) => method === 'post' && /\/ai\/drug-interactions/.test(url),
    handler: (data) => ({
      interactions: [],
      note: 'Analisi locale: nessuna interazione critica rilevata nel dataset offline. Verifica sempre con AIFA per casi complessi.',
      drugs: data?.drugs || data?.drug_codes || [],
      _mock: true,
    }),
  },

  // ---- Doctor Insights & ICD-10 ----
  {
    match: (url, method) => method === 'get' && /\/ai\/insights\/doctor$/.test(url),
    handler: () => mocks.mockDoctorInsights(),
  },
  {
    match: (url, method) => method === 'post' && /\/ai\/icd10-suggest$/.test(url),
    handler: (data) => {
      const t = (data?.clinical_note || '').toLowerCase();
      const codes = [];
      if (/\b(iperten|pressione alta)\b/.test(t)) codes.push({ code: 'I10', label: 'Ipertensione essenziale' });
      if (/\b(diabet|glicemia)\b/.test(t)) codes.push({ code: 'E11', label: 'Diabete mellito tipo 2' });
      if (/\b(asma|dispnea)\b/.test(t)) codes.push({ code: 'J45', label: 'Asma' });
      if (/\b(gastri|reflusso)\b/.test(t)) codes.push({ code: 'K29', label: 'Gastrite' });
      if (!codes.length) codes.push({ code: 'Z00', label: 'Visita generale senza reperti' });
      return { suggestions: codes, _mock: true };
    },
  },

  // ---- No-show Score ----
  {
    match: (url, method) => method === 'post' && /\/ai\/noshow-score/.test(url),
    handler: () => ({ score: 0.12, band: 'low', _mock: true }),
  },

  // ---- Chat AI ----
  {
    match: (url, method) => method === 'post' && /\/ai\/(chat|sessions)/.test(url),
    handler: (data) => mocks.mockChatResponse({ message: data?.message || '', history: data?.history || [] }),
  },
  {
    match: (url, method) => method === 'get' && /\/ai\/sessions$/.test(url),
    handler: () => ({ sessions: [], _mock: true }),
  },

  // ---- Daily briefing ----
  {
    match: (url, method) => method === 'get' && /\/(briefing|daily-briefing|feed\/briefing)/.test(url),
    handler: () => mocks.mockDailyBriefing({ appointments_today: 0, unread_messages: 0, alerts: 0 }),
  },

  // ---- Care Team AI (Fase 23) ----
  {
    match: (url, method) => method === 'post' && /\/care-team\/[^/]+\/ai\/decline-score$/.test(url),
    handler: (data, url) => mocks.mockCareDeclineScore({ teamId: url.split('/')[2], days: data?.days, scales: data?.scales }),
  },
  {
    match: (url, method) => method === 'post' && /\/care-team\/[^/]+\/ai\/family-update$/.test(url),
    handler: (data, url) => mocks.mockCareFamilyUpdate({ teamId: url.split('/')[2], shift_notes: data?.shift_notes }),
  },
  {
    match: (url, method) => method === 'get' && /\/care-team\/[^/]+\/ai\/reports$/.test(url),
    handler: () => ({ items: [], count: 0, _mock: true }),
  },

  // ---- Video promo (Fal.ai) ----
  {
    match: (url, method) => method === 'get' && /\/video-promo\/presets$/.test(url),
    handler: () => mocks.mockVideoPromoPresets(),
  },
  {
    match: (url, method) => method === 'post' && /\/video-promo\/generate$/.test(url),
    handler: () => mocks.mockVideoPromoGenerate(),
  },
  {
    match: (url, method) => method === 'get' && /\/video-promo(\/|$)/.test(url),
    handler: () => ({ items: [], count: 0, _mock: true }),
  },
];

if (WHITELABEL_MODE) {
  api.interceptors.request.use((config) => {
    const method = (config.method || 'get').toLowerCase();
    const url = (config.url || '').split('?')[0];
    const hit = AI_ROUTE_HANDLERS.find((h) => h.match(url, method));
    if (!hit) return config;
    // Short-circuit: throw a synthetic response so we never leave the browser.
    const mockResponse = {
      data: hit.handler(config.data || {}, url),
      status: 200,
      statusText: 'OK (whitelabel mock)',
      headers: { 'x-whitelabel-mock': '1' },
      config,
      request: null,
    };
    // Reject with a special marker; the response interceptor below converts it.
    return Promise.reject({ __whitelabelMock: mockResponse });
  });
  api.interceptors.response.use(
    (r) => r,
    (err) => (err && err.__whitelabelMock ? Promise.resolve(err.__whitelabelMock) : Promise.reject(err)),
  );
}

// ---------------------------------------------------------------------------
//  GESTIONE ERRORI GLOBALE (UX)
//  - Rete assente / server irraggiungibile: un solo toast ogni 15s (niente spam),
//    l'errore viene marcato `isNetworkError` così i componenti possono mostrare
//    il fallback offline senza rompere l'interfaccia.
//  - 5xx: toast discreto; i 4xx restano ai chiamanti (messaggi specifici).
// ---------------------------------------------------------------------------
let _lastNetToast = 0;
const NET_TOAST_EVERY_MS = 15_000;

/** Messaggio leggibile per l'utente a partire da un errore axios/JS. */
export const describeError = (err, fallback = 'Operazione non riuscita') => {
  if (!err) return fallback;
  if (err.isNetworkError || err.code === 'ERR_NETWORK' || (!err.response && err.request)) {
    return typeof navigator !== 'undefined' && navigator.onLine === false
      ? 'Sei offline: i dati salvati in locale verranno sincronizzati al ritorno della rete'
      : 'Server non raggiungibile: riprova tra qualche istante';
  }
  if (err.code === 'ECONNABORTED') return 'Tempo di attesa esaurito: riprova';
  const d = err.response?.data?.detail;
  if (typeof d === 'string' && d) return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  if (err.response?.status >= 500) return 'Errore del server: riprova più tardi';
  return err.message && !/^Request failed/.test(err.message) ? err.message : fallback;
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err && err.__whitelabelMock) return Promise.reject(err);
    const isNetwork = !!err && !err.response && (err.code === 'ERR_NETWORK' || !!err.request);
    if (isNetwork) {
      err.isNetworkError = true;
      const now = Date.now();
      if (now - _lastNetToast > NET_TOAST_EVERY_MS) {
        _lastNetToast = now;
        toast.warning(describeError(err), { id: 'net-offline', duration: 5000 });
      }
    }
    // Quota AI mensile esaurita (402 dal meter multi-tenant): un solo toast, evento per la UI.
    if (err?.response?.status === 402 && /traffic limit/i.test(String(err.response?.data?.error || ''))) {
      err.isAiQuotaExceeded = true;
      toast.error('Limite mensile di richieste AI raggiunto per questa clinica.', {
        id: 'ai-quota', duration: 10000,
        description: 'L\'amministratore pu\u00f2 passare a un piano superiore: il limite si alza subito dopo il pagamento.',
        action: { label: 'Aggiorna piano', onClick: () => { window.location.assign('/admin/ai-usage#upgrade'); } },
      });
      try { window.dispatchEvent(new CustomEvent('sn:ai-quota-exceeded', { detail: err.response.data })); } catch { /* noop */ }
    }
    // Sessione scaduta/revocata: notifica l'AuthProvider così lo stato utente torna
    // anonimo subito (e le protezioni anti-tamper si riattivano istantaneamente).
    if (err?.response?.status === 401 && typeof window !== 'undefined') {
      const url = String(err.config?.url || '');
      if (!/\/auth\/(login|register|magic|me)\b/.test(url)) {
        try { window.dispatchEvent(new CustomEvent('sn:unauthorized', { detail: { url } })); } catch { /* noop */ }
      }
    }
    return Promise.reject(err);
  },
);

export default api;
